import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";

const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";

/** Session flag: the id of the last question the guest hid the resume row for. */
export const RESUME_DISMISSED_KEY = "mj-ai-pill-resume-dismissed";

function readDismissed(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(RESUME_DISMISSED_KEY);
  } catch {
    return null;
  }
}

/**
 * Whether the guest has hidden "Continue your chat" for this question.
 *
 * Keyed to the message id rather than a plain on/off, so hiding it lasts for
 * the rest of the session but a new question brings it back. Read lazily: on
 * the server and on the first client render there are no messages yet, so the
 * flag cannot change the hydrated markup.
 */
export function useResumeDismissed(messageId: string | null) {
  const [dismissedId, setDismissedId] = useState<string | null>(readDismissed);
  const dismissed = messageId !== null && dismissedId === messageId;
  const dismiss = useCallback(() => {
    if (messageId === null) return;
    setDismissedId(messageId);
    try {
      window.sessionStorage.setItem(RESUME_DISMISSED_KEY, messageId);
    } catch {
      // Private mode: it stays hidden until the next reload, which is fine.
    }
  }, [messageId]);
  return [dismissed, dismiss] as const;
}

/**
 * Pewter kit #05 (owner, 2026-10-09): the pill's "go back to your chat"
 * state. Two lines, never cut mid-word: a title, then the last question
 * truncated by CSS with an ellipsis. A navy coin with a gold history mark at
 * the start, the reopen button, and an X that hides this state.
 */
export function AiPillResume({
  question,
  tabIndex,
  isRtl,
  onOpen,
  onDismiss,
  action,
}: {
  question: string;
  tabIndex: number;
  isRtl: boolean;
  onOpen: () => void;
  onDismiss: () => void;
  /** The pill's own reopen button, kept so its place and look do not move. */
  action: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="mj-ai-resume-in relative flex h-14 items-center gap-1.5 ps-2 pe-1.5" dir={isRtl ? "rtl" : "ltr"}>
      <button
        type="button"
        tabIndex={tabIndex}
        onClick={onOpen}
        aria-label={t("ai_pill.resume", { q: question })}
        className="flex h-full min-w-0 flex-1 items-center gap-3 rounded-full text-start"
      >
        <span
          aria-hidden="true"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: NAVY_GRADIENT }}
        >
          <Icon name="ri-history-line" className="text-[16px] text-[#E5B265]" strokeWidth={2.4} />
        </span>
        <span className="min-w-0 flex-1">
          {/* The title wraps rather than cuts ("Setzen Sie Ihren Chat fort"); two lines still fit the 56px pill. */}
          <span className="block break-words text-[14px] font-semibold leading-tight text-[#110E39]">
            {t("ai_pill.resume_title")}
          </span>
          {/* clamp-ok: guest's own typed question, the full chat opens on tap */}
          <span className="mt-0.5 block truncate text-[12.5px] leading-tight text-[#5a5d77]">{question}</span>
        </span>
      </button>
      {action}
      <button
        type="button"
        tabIndex={tabIndex}
        onClick={onDismiss}
        aria-label={t("ai_pill.resume_dismiss")}
        title={t("ai_pill.resume_dismiss")}
        className="mj-press grid h-9 w-9 shrink-0 place-items-center rounded-full text-[#5a5d77] transition-colors duration-150 hover:bg-[rgba(17,14,57,0.055)] hover:text-[#110E39]"
      >
        <Icon name="ri-close-line" className="text-[17px]" aria-hidden="true" />
      </button>
    </div>
  );
}
